import { Button } from "@/components/ui/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/shadcn/dropdown-menu";
import {
  CommandWorkspaceLayout,
  WorkspaceNavItem,
} from "./layouts/CommandWorkspaceLayout";
import { Home, Users, LogOut, UserPen } from "lucide-react";
import { Outlet, useNavigate } from "react-router";
import { useCallback, useMemo } from "react";
import { useAuth } from "@/components/contexts/AuthContext/UseAuth";

const adminNavLinks: WorkspaceNavItem[] = [
  {
    label: "Dashboard",
    path: "/",
    Icon: Home,
  },
  {
    label: "Leads",
    path: "/leads",
    Icon: Users,
  },
];

const buildProfileFooter = (
  firstName: string | undefined,
  lastName: string | undefined,
  email: string | undefined,
  onViewProfile: () => void,
  onSignOut: () => void,
) => {
  return (collapsed: boolean) => {
    if (!firstName || !lastName || !email) {
      return (
        <div className="flex items-center gap-3 text-sm text-gray-500">
          Loading profile…
        </div>
      );
    }

    const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();

    if (collapsed) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              {initials}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56"
            side="top"
            align="start"
            alignOffset={0}
            sideOffset={12}
          >
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-gray-900">
                {firstName} {lastName}
              </p>
              <p className="text-xs text-gray-500">{email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onViewProfile} className="gap-2 text-sm">
              <UserPen className="h-4 w-4" />
              Edit profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onSignOut}
              className="gap-2 text-sm text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
          {initials}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900">
            {firstName} {lastName}
          </span>
          <span className="text-xs text-gray-500">{email}</span>
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="link"
              className="px-0 text-xs font-medium"
              onClick={onViewProfile}
            >
              View profile
            </Button>
            <Button
              variant="link"
              className="px-0 text-xs font-medium text-destructive"
              onClick={onSignOut}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  };
};

export function Layout() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const navFooter = useMemo(
    () =>
      buildProfileFooter(
        "Admin",
        "User",
        "admin@rentinit.ae",
        () => navigate("/profile"), // TODO: Update to admin profile route when available
        handleLogout,
      ),
    [handleLogout, navigate],
  );

  return (
    <CommandWorkspaceLayout
      navItems={adminNavLinks}
      navFooter={navFooter}
      title="Admin workspace"
    >
      <Outlet />
    </CommandWorkspaceLayout>
  );
}
